/** /songs/:songId — detail (WS1), lyrics (WS2), song clips from official lines (WS5), pipeline steps with rerun
 * (spine), analysis (WS3), TikTok usage (WS4), linked videos. Each section loads and fails independently. */
import { mediaDownloadUrl, mediaUrl } from "../../api/client";
import { useAnnounce } from "../../components/announce";
import { PageHeader } from "../../components/PageHeader";
import { type Patch, useUrlState } from "../lib/url";
import { LyricsSection } from "./LyricsSection";
import { SongClipsSection } from "./SongClipsSection";
import { AnalysisSection, DetailSection, LinkedVideos, StepsSection, TikTokSection, useSong } from "./SongSections";

const COVER_SOURCE: Record<string, string> = { spotify: "Spotify", ytmusic: "YouTube Music", mv: "music video" };

/** The song's cover picture (160 px) with a download link; nothing until `song.cover` has stored one. */
export function SongCover({ title, mediaId, source }: { title: string; mediaId: number; source?: string | null }) {
  return (
    <figure className="ws7-cover" data-testid="song-cover">
      <img src={mediaUrl(mediaId)} alt={`Cover of ${title}`} width={160} height={160} />
      <figcaption>
        <a href={mediaDownloadUrl(mediaId)} download data-testid="song-cover-download" aria-label={`Download the cover of ${title}`}>
          Download cover
        </a>
        {source && <span className="ws7-muted"> · from {COVER_SOURCE[source] ?? source}</span>}
      </figcaption>
    </figure>
  );
}

export function SongView({ songId }: { songId: number }) {
  const announce = useAnnounce();
  const { sp, set: setUrl } = useUrlState();
  const song = useSong(songId);
  const set = (p: Patch, msg?: string) => {
    setUrl(p);
    if (msg) announce(msg);
  };
  const title = song.data ? song.data.title : `Song #${songId}`;
  return (
    <div className="ws7-page" data-testid="song-page">
      <PageHeader title={title} md={{ page: "song", params: { id: songId } }}
        lead={song.data?.cover_media_id != null
          ? <SongCover title={song.data.title} mediaId={song.data.cover_media_id} source={song.data.cover_source} />
          : undefined} />
      <div className="ws7-grid2">
        <DetailSection song={song} />
        <LinkedVideos ids={song.data?.video_ids ?? []} />
      </div>
      <StepsSection entity="song" id={songId} />
      <LyricsSection songId={songId} sp={sp} set={set} />
      <SongClipsSection songId={songId} raw={sp.get("text") === "raw"} />
      <div className="ws7-grid2">
        <AnalysisSection songId={songId} />
        <TikTokSection songId={songId} />
      </div>
    </div>
  );
}
